import React from "react";
import { connect } from "react-redux";

import List from "../core/components/list/List";
import ListItem from "../core/components/list/ListItem";
import { Alignment, Side, Size } from "../core/types/system/Layout";
import GeneralContainer from "../core/components/containers/GeneralContainer";
import { addAlert } from "../core/state/system/Alerts";
import { AlertTypes } from "../core/types/system/AlertEntry";
import { unwrappedDispatch } from "../core/foundation/Dispatching";
import { setPageLoading } from "../core/state/system/UIConfiguration";
import User from "../core/types/core/User";
import {
    approveApprovals,
    initialiseApprovalsDetails,
    rejectApprovals,
} from "../core/state/administration/Approvals";
import Approval from "../core/types/admin/Approval";
import Dialog from "../core/components/dialog/Dialog";
import DialogFooter from "../core/components/dialog/DialogFooter";
import DialogOK from "../core/components/dialog/DialogOK";
import DialogCancel from "../core/components/dialog/DialogCancel";
import DialogContent from "../core/components/dialog/DialogContent";
import DialogBad from "../core/components/dialog/DialogBad";
import Text from "../core/components/text/Text";
import { IAppState } from "../core/state/Store";

import "./Approvals.scss";
import { initialiseAllUserDetails } from "../core/state/core/User";
import Panel from "../core/components/panels/Panel";
import Button from "../core/components/buttons/Button";

interface ApprovalsProps {
    user: User;
    users: User[];
    approvals: Approval[];

    setPageLoading: (loading: boolean) => void;
    initialiseApprovalsDetails: () => Promise<void>;
    initialiseAllUserDetails: () => Promise<void>;
    approveApprovals: (approval: Approval) => Promise<void>;
    rejectApprovals: (approval: Approval) => Promise<void>;
}

interface ApprovalsState {
    approval?: Approval;
    approvalOpen?: boolean;
    approvalLoading?: boolean;
}

class Approvals extends React.Component<ApprovalsProps, ApprovalsState> {
    state: ApprovalsState = {
        approvalOpen: false,
        approvalLoading: false,
    };

    async componentDidMount() {
        this.props.setPageLoading(true);
        await Promise.all([
            this.props.initialiseApprovalsDetails(),
            this.props.initialiseAllUserDetails(),
        ]);
        this.props.setPageLoading(false);
    }

    typeDisplay(type: string): string {
        switch (type) {
            case "TimetablePublish":
                return "Timetable Publish";
            case "TimetableRecycle":
                return "File Recycle";
            case "TimetableRestore":
                return "File Restore";
            default:
                return "Unknown Type";
        }
    }

    userDisplay(userId: number): string {
        return (
            this.props.users?.find(user => user.id === userId)?.display ??
            "Unknown User"
        );
    }

    view(approval: Approval) {
        this.setState({
            approval: approval,
            approvalOpen: true,
        });
    }

    allowApprove(): boolean {
        return (
            !this.state.approval?.approvedOn && !this.state.approval?.rejectedOn
        );
    }

    async approve() {
        try {
            const result = await addAlert({
                type: AlertTypes.Confirm,
                icon: "warning",
                styleColor: "yellow",
                title: "Confirm Approval",
                text:
                    "Are you sure you want to approve this request? " +
                    "This action cannot be undone.",
                okText: "Approve",
            });

            if (result.response) {
                this.setState({
                    approvalLoading: true,
                });

                await this.props.approveApprovals(this.state.approval!);

                addAlert({
                    title: "Approval processed successfully",
                    text:
                        "The approval has been processed and any actions " +
                        "related to the approval have been initiated.",
                });

                this.setState({
                    approval: undefined,
                    approvalOpen: false,
                });
            }
        } finally {
            this.setState({
                approvalLoading: false,
            });
        }
    }

    async reject() {
        try {
            const result = await addAlert({
                icon: "warning",
                styleColor: "yellow",
                title: "Confirm Rejection",
                text:
                    "Are you sure you want to reject this request? " +
                    "This action cannot be undone.",
                okText: "Reject",
                prompts: [
                    {
                        type: "text",
                        display: "Rejection Reason",
                        value: "Request rejected",
                    },
                ],
            });

            if (result.response) {
                this.setState({
                    approvalLoading: true,
                });

                await this.props.rejectApprovals({
                    ...this.state.approval,
                    rejectionReason: result.promptResults?.[0].value as string,
                });

                addAlert({
                    title: "Rejection processed successfully",
                    text:
                        "The rejection has been processed and any actions " +
                        "related to the rejection have been performed.",
                });

                this.setState({
                    approval: undefined,
                    approvalOpen: false,
                });
            }
        } finally {
            this.setState({
                approvalLoading: false,
            });
        }
    }

    render() {
        return (
            <>
                <section data-contextkey="approvals-menu">
                    {this.props.approvals?.length > 0 ? (
                        <GeneralContainer
                            scaling={true}
                            mid={true}
                            max={true}
                            bufferSide={Side.All}
                            clearanceSide={Side.Top}
                            clearanceSize={Size.Large}
                            alignment={Alignment.Center}>
                            <List<Approval> joined={true}>
                                {this.props.approvals.map((approval, index) => (
                                    <ListItem<Approval>
                                        key={index}
                                        data={approval}
                                        panel={true}
                                        autoHeight={true}
                                        selectable={true}
                                        bufferSide={Side.All}
                                        contextkey="approvals-file"
                                        contextvalue={approval.id}>
                                        <div className="twothirds flex-column">
                                            <h3 className="truncated">
                                                {this.typeDisplay(
                                                    approval.type,
                                                )}
                                            </h3>

                                            <div className="top-buffer-small flex-column spaced">
                                                <div>
                                                    <b>Created On:&nbsp;</b>
                                                    {approval.createdOn
                                                        .toDate()
                                                        .toLocaleShort()}
                                                    &nbsp; &nbsp;
                                                    <b>Created By:&nbsp;</b>
                                                    {this.userDisplay(
                                                        approval.createdBy,
                                                    )}
                                                    &nbsp;
                                                </div>
                                            </div>
                                        </div>

                                        <div className="onethird text-right flex-column">
                                            {!approval.approvedOn &&
                                                !approval.rejectedOn && (
                                                    <div>
                                                        <b>Approval Required</b>
                                                    </div>
                                                )}
                                            {!!approval.approvedOn && (
                                                <div>
                                                    <b>Approved:&nbsp;</b>
                                                    {approval.approvedOn
                                                        ? approval.approvedOn
                                                              .toDate()
                                                              .toLocaleShort()
                                                        : null}
                                                </div>
                                            )}
                                            {!!approval.rejectedOn && (
                                                <div>
                                                    <b>Rejected On:&nbsp;</b>
                                                    {approval.rejectedOn
                                                        ? approval.rejectedOn
                                                              .toDate()
                                                              .toLocaleShort()
                                                        : null}
                                                </div>
                                            )}

                                            <Button
                                                icon="search"
                                                inline={true}
                                                transparent={true}
                                                align={Alignment.Right}
                                                clearanceSide={Side.Top}
                                                clearanceSize={Size.Small}
                                                onClick={this.view.bind(
                                                    this,
                                                    approval,
                                                )}
                                                tooltip="View the details of this approval"
                                            />
                                        </div>
                                    </ListItem>
                                ))}
                            </List>
                        </GeneralContainer>
                    ) : (
                        <Text clearanceSide={Side.All}>
                            No approvals found.
                        </Text>
                    )}
                </section>

                <Dialog
                    canResize={false}
                    showDialog={this.state.approvalOpen}
                    showLoading={this.state.approvalLoading}
                    dialogIcon="done_outline"
                    dialogTitle="Approval Details"
                    zIndex={1000}
                    width={850}>
                    <DialogContent>
                        <Panel
                            title={this.typeDisplay(this.state.approval?.type)}>
                            <Panel clearanceSide={Side.Top}>
                                <b>Created On:&nbsp;</b>
                                {this.state.approval?.createdOn
                                    .toDate()
                                    .toLocaleShort()}
                                &nbsp; &nbsp;<b>Created By:&nbsp;</b>
                                {this.userDisplay(
                                    this.state.approval?.createdBy,
                                )}
                                &nbsp;
                            </Panel>

                            <Panel clearanceSide={Side.Top}>
                                <div
                                    dangerouslySetInnerHTML={{
                                        __html:
                                            this.state.approval?.details ?? "",
                                    }}></div>
                            </Panel>

                            {!!this.state.approval?.rejectedOn && (
                                <Panel clearanceSide={Side.Top}>
                                    <b>Rejection Reason:&nbsp;</b>
                                    {this.state.approval?.rejectionReason}
                                </Panel>
                            )}

                            {this.state.approval?.type === "TimetablePublish" &&
                                !(
                                    this.state.approval?.approvedOn ||
                                    this.state.approval?.rejectedOn
                                ) && (
                                    <Panel clearanceSide={Side.Top}>
                                        <b>Note:&nbsp;</b>
                                        Rejecting this approval will remove the
                                        associated published timetable from the
                                        system.
                                    </Panel>
                                )}

                            <Panel clearanceSide={Side.Top}>
                                {!this.state.approval?.approvedOn &&
                                    !this.state.approval?.rejectedOn && (
                                        <div>
                                            <b>Approval Required</b>
                                        </div>
                                    )}
                                {!!this.state.approval?.approvedOn && (
                                    <div>
                                        <b>Approved On:&nbsp;</b>
                                        {this.state.approval?.approvedOn
                                            .toDate()
                                            .toLocaleShort()}
                                        &nbsp;&nbsp;
                                        <b>Approved By:&nbsp;</b>
                                        {this.userDisplay(
                                            this.state.approval?.approvedBy,
                                        )}
                                    </div>
                                )}
                                {!!this.state.approval?.rejectedOn && (
                                    <div>
                                        <b>Rejected On:&nbsp;</b>
                                        {this.state.approval?.rejectedOn
                                            .toDate()
                                            .toLocaleShort()}
                                        &nbsp;&nbsp;
                                        <b>Rejected By:&nbsp;</b>
                                        {this.userDisplay(
                                            this.state.approval?.rejectedBy,
                                        )}
                                    </div>
                                )}
                            </Panel>
                        </Panel>
                    </DialogContent>
                    <DialogFooter>
                        <DialogCancel
                            text="Close"
                            onClick={() =>
                                this.setState({ approvalOpen: false })
                            }
                        />
                        {this.allowApprove() && (
                            <>
                                <DialogBad
                                    text="Reject"
                                    onClick={this.reject.bind(this)}
                                />
                                <DialogOK
                                    text="Approve"
                                    onClick={this.approve.bind(this)}
                                />
                            </>
                        )}
                    </DialogFooter>
                </Dialog>
            </>
        );
    }
}

const mapStateToProps = (state: IAppState) => {
    const { approvals, user } = state.db;
    const { session } = state.session;
    return {
        user: session.user,
        users: user.allUsers,
        approvals: approvals.approvals,
    };
};

const mapDispatchToProps = dispatch => {
    return {
        setPageLoading: (loading: boolean) => dispatch(setPageLoading(loading)),
        initialiseApprovalsDetails: () => initialiseApprovalsDetails(),
        initialiseAllUserDetails: () =>
            unwrappedDispatch(initialiseAllUserDetails()),
        approveApprovals: (approval: Approval) =>
            unwrappedDispatch(approveApprovals(approval)),
        rejectApprovals: (approval: Approval) =>
            unwrappedDispatch(rejectApprovals(approval)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(Approvals);
